import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Bar, BarChart, CartesianGrid, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Check } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { money, compactMoney, pct } from "../lib/format";
import { RANGE_PRESETS, quarterOptions, trailingQuarters } from "../lib/quarters";
import { ChartCard, VENDOR_COLORS } from "../components/ChartCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const axisTick = { fontSize: 11, fill: "#6B7A82" };
const QUARTERS = quarterOptions();
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const PRODUCTS = ["ALL", "AC", "CI", "HI", "PP"];

const periodTick = (value) =>
  String(value).includes("-Q") ? value : `${MONTHS[Number(String(value).slice(5, 7)) - 1]} '${String(value).slice(2, 4)}`;

export default function AdminCompare() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fallback = useMemo(() => trailingQuarters(8), []);
  const from = params.get("from") || fallback.from;
  const to = params.get("to") || fallback.to;
  const metric = params.get("metric") || "premium";
  const product = params.get("product") || "ALL";
  const planType = params.get("plan") || "";
  const insurer = params.get("insurer") || "";
  const carrier = params.get("carrier") || "";
  const granularity = params.get("gran") === "month" ? "month" : "quarter";
  const selectedKey = params.get("companies") || "";
  const selected = selectedKey.split(",").filter(Boolean);

  const [allCompanies, setAllCompanies] = useState([]);
  const [filterLists, setFilterLists] = useState({ plan_types: [], insurers: [], metrics: [], carriers: [] });
  const [carrierMap, setCarrierMap] = useState({});

  const setControl = useCallback((changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value); else next.delete(key);
    });
    setParams(next, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    api.get("/financials/dashboard", { params: { from_quarter: from, to_quarter: to } })
      .then(({ data: payload }) => {
        setAllCompanies(payload.companies || []);
        setFilterLists((current) => ({ ...current, plan_types: payload.plan_types || [], insurers: payload.insurers || [], carriers: payload.carriers || [] }));
      })
      .catch(() => {});
  }, [from, to]);

  useEffect(() => {
    api.get("/admin/carriers")
      .then(({ data }) => setCarrierMap(Object.fromEntries(
        data.map((item) => [item.name, item.employers.map((link) => link.company)]))))
      .catch(() => setCarrierMap({}));
  }, []);

  useEffect(() => {
    if (!selectedKey) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    api.get("/financials/compare", {
      params: { companies: selectedKey, metric, from_quarter: from, to_quarter: to,
                product, plan_type: planType, insurer, carrier, granularity },
    })
      .then(({ data: payload }) => {
        if (cancelled) return;
        setData(payload);
        setFilterLists((current) => ({
          ...current, metrics: payload.metrics,
          plan_types: payload.plan_types || current.plan_types,
          insurers: payload.insurers || current.insurers,
        }));
      })
      .catch((error) => toast.error(formatApiError(error.response?.data?.detail)))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedKey, metric, from, to, product, planType, insurer, carrier, granularity]);

  const toggleCompany = (name) => {
    const next = selected.includes(name)
      ? selected.filter((item) => item !== name)
      : [...selected, name].slice(0, 8);
    setControl({ companies: next.join(",") });
  };

  const isPercent = Boolean(data?.is_percent);
  const format = (value) => (isPercent ? pct(value) : money(value));

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10" data-testid="admin-compare-page">
      <div className="mb-8">
        <div className="eyebrow mb-2">Administration</div>
        <h1 className="font-heading font-black text-5xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>
          Compare clients.
        </h1>
        <p className="mt-3 text-[15px] max-w-2xl" style={{ color: "var(--muted)" }}>
          Put up to eight clients side by side on one metric for book-level reviews. Every control is
          shareable in the URL.
        </p>
      </div>

      <div className="border p-5 mb-8" style={{ borderColor: "var(--border)", background: "#F3F3F1" }} data-testid="compare-controls">
        <div className="eyebrow text-[9px] mb-3">Employers ({selected.length}/8)</div>
        <div className="flex flex-wrap gap-2 mb-5">
          {(carrier ? allCompanies.filter((name) => (carrierMap[carrier] || []).includes(name)) : allCompanies).map((name) => {
            const active = selected.includes(name);
            return (
              <button
                key={name}
                onClick={() => toggleCompany(name)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs border"
                style={{
                  borderColor: "var(--border)",
                  background: active ? "var(--ink)" : "transparent",
                  color: active ? "var(--surface)" : "var(--muted)",
                }}
                data-testid={`compare-company-${name}`}
              >
                {active && <Check size={12} />} {name}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <ControlSelect label="Metric" value={metric} onChange={(value) => setControl({ metric: value })}
                         options={(filterLists.metrics.length ? filterLists.metrics : [{ key: "premium", label: "Paid Premium" }]).map((item) => item.key)}
                         labels={Object.fromEntries((filterLists.metrics || []).map((item) => [item.key, item.label]))}
                         testId="compare-metric" />
          <ControlSelect label="From" value={from} onChange={(value) => setControl({ from: value })}
                         options={QUARTERS} testId="compare-from" />
          <ControlSelect label="To" value={to} onChange={(value) => setControl({ to: value })}
                         options={QUARTERS} testId="compare-to" />
          <ControlSelect label="Plan type" value={data?.plan_type || planType}
                         onChange={(value) => setControl({ plan: value, product: "", insurer: "" })}
                         options={filterLists.plan_types} testId="compare-plan-type" />
          <ControlSelect label="Product" value={data?.product || product}
                         onChange={(value) => setControl({ product: value })}
                         options={data?.products || PRODUCTS} testId="compare-product" />
          <ControlSelect label="Carrier" value={carrier || "__all__"}
                         onChange={(value) => setControl({ carrier: value === "__all__" ? "" : value })}
                         options={["__all__", ...(filterLists.carriers || [])]}
                         labels={{ __all__: "All Carriers" }} testId="compare-carrier" />
          <ControlSelect label="Insurer" value={insurer || "__all__"}
                         onChange={(value) => setControl({ insurer: value === "__all__" ? "" : value })}
                         options={["__all__", ...filterLists.insurers]}
                         labels={{ __all__: "All Insurers" }} testId="compare-insurer" />
          <div>
            <div className="eyebrow text-[9px] mb-2">Detail</div>
            <div className="flex items-center border h-9" style={{ borderColor: "var(--border)" }} data-testid="compare-granularity">
              {[["quarter", "Quarterly"], ["month", "Monthly"]].map(([value, label]) => (
                <button key={value} onClick={() => setControl({ gran: value === "quarter" ? "" : value })}
                        className="px-3 h-full text-xs"
                        style={{ background: granularity === value ? "var(--ink)" : "transparent",
                                 color: granularity === value ? "var(--surface)" : "var(--muted)" }}
                        data-testid={`compare-granularity-${value}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-1">
            {RANGE_PRESETS.map((preset) => {
              const resolved = preset.resolve();
              const active = resolved.from === from && resolved.to === to;
              return (
                <button key={preset.key} onClick={() => setControl({ from: resolved.from, to: resolved.to })}
                        className="px-3 py-2 text-xs border"
                        style={{ borderColor: "var(--border)",
                                 background: active ? "var(--ink)" : "transparent",
                                 color: active ? "var(--surface)" : "var(--muted)" }}
                        data-testid={`compare-preset-${preset.key}`}>
                  {preset.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {!selected.length && (
        <div className="border p-16 text-center text-sm" style={{ borderColor: "var(--border)", color: "var(--muted)" }} data-testid="compare-pick-prompt">
          Pick one or more clients above to build the comparison.
        </div>
      )}

      {selected.length > 0 && (
        <ChartCard
          testId="compare-chart" subtitle="Book-level comparison"
          title={`${data?.metric_label || "Metric"} by employer`}
          note={`${selected.length} employer(s), ${granularity === "month" ? "monthly" : "quarterly"} buckets, ${product === "ALL" ? "all products" : product}.`}
          baseName={`compare-${metric}`}
          hasData={Boolean(data?.has_data)}
          rows={data?.rows || []}
          columns={[{ key: "period", label: "Period" },
                    ...(data?.companies || []).map((name) => ({ key: name, label: name }))]}
        >
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.rows || []}>
                <CartesianGrid stroke="#EFEEE9" vertical={false} />
                <XAxis dataKey="period" tick={axisTick} axisLine={false} tickLine={false}
                       tickFormatter={periodTick} interval="preserveStartEnd" />
                <YAxis tickFormatter={isPercent ? (value) => `${value}%` : compactMoney}
                       tick={axisTick} axisLine={false} tickLine={false} width={isPercent ? 48 : 62} />
                <Tooltip
                  cursor={{ fill: "rgba(14,58,92,0.06)" }}
                  formatter={(value) => format(value)}
                  labelFormatter={periodTick}
                  contentStyle={{ borderRadius: 0, border: "1px solid #D8D6D1", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {isPercent && data?.target_loss_ratio && (
                  <ReferenceLine y={data.target_loss_ratio} stroke="#B4472D" strokeDasharray="4 4" ifOverflow="extendDomain"
                                 label={{ value: `Target ${Number(data.target_loss_ratio).toFixed(0)}%`,
                                          position: "insideTopRight", fontSize: 10, fill: "#B4472D" }} />
                )}
                {(data?.companies || []).map((name, index) => (
                  <Bar key={name} dataKey={name} name={name}
                       fill={VENDOR_COLORS[index % VENDOR_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      )}

      {data?.summary?.length > 0 && (
        <div className="mt-8 border" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="compare-summary">
          <div className="px-6 py-4 border-b eyebrow" style={{ borderColor: "var(--border)" }}>
            {isPercent ? "Average over the range" : "Total over the range"}
          </div>
          <div className="divide-y" style={{ borderColor: "var(--border)" }}>
            {data.summary.map((item, index) => (
              <div key={item.company} className="flex items-center justify-between px-6 py-4 text-sm" data-testid={`compare-total-${item.company}`}>
                <span className="flex items-center gap-3" style={{ color: "var(--ink)" }}>
                  <span className="inline-block w-3 h-3" style={{ background: VENDOR_COLORS[(data.companies.indexOf(item.company)) % VENDOR_COLORS.length] }} />
                  <span className="font-medium">{item.company}</span>
                  <span className="eyebrow text-[9px]">#{index + 1}</span>
                </span>
                <span className="font-heading font-bold text-lg" style={{ color: "var(--ink)" }}>
                  {format(item.total)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="mt-4 text-xs" style={{ color: "var(--muted)" }} data-testid="compare-loading">Updating…</div>
      )}
    </div>
  );
}

function ControlSelect({ label, value, onChange, options, labels = {}, testId }) {
  if (!options.length) return null;
  return (
    <div>
      <div className="eyebrow text-[9px] mb-2">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="!rounded-none !bg-white min-w-[132px] h-9 text-xs" data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="!rounded-none max-h-72">
          {options.map((option) => (
            <SelectItem key={option} value={option} className="!rounded-none text-xs">
              {labels[option] || option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
